import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ASSETS } from "@/constants/assets";

const COUNTRIES = [
  "République Démocratique du Congo",
  "Cameroun",
  "Canada",
  "France",
  "Belgique",
  "Sénégal",
  "Côte d'Ivoire",
  "Bénin",
  "Togo",
  "Gabon",
  "Congo-Brazzaville",
  "Mali",
  "Burkina Faso",
  "Guinée",
  "Niger",
  "Tchad",
  "Rwanda",
  "Burundi",
  "Maroc",
  "Tunisie",
  "Algérie",
  "Suisse",
  "Luxembourg",
  "Haïti",
  "États-Unis",
  "Autre",
];

export default function LeadCaptureModal({ open, onClose, onSuccess }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    whatsapp: "",
    country: "",
    city: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/prospects", form);
      localStorage.setItem("ph_prospect_id", data.id);
      localStorage.setItem("ph_prospect_email", data.email);
      toast.success("Coordonnées enregistrées. Créez votre accès.");
      onSuccess(data);
    } catch (err) {
      toast.error(formatApiError(err, "Vérifiez vos informations."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40 backdrop-blur-xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="lead-capture-overlay"
        >
          <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 16, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-2xl ph-card rounded-sm overflow-hidden"
            data-testid="lead-capture-modal"
          >
            {onClose && (
              <button
                onClick={onClose}
                aria-label="Fermer"
                className="absolute right-4 top-4 text-[#667085] hover:text-[#9A762F] transition-colors z-10"
                data-testid="lead-capture-close"
              >
                <X size={20} />
              </button>
            )}

            <div className="grid md:grid-cols-5">
              <div className="hidden md:block md:col-span-2 relative">
                <img
                  src={ASSETS.bookVertical}
                  alt="Guide Prospecter sans se faire rejeter"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#F8F9FB] via-transparent to-transparent" />
              </div>

              <div className="md:col-span-3 p-8 md:p-10">
                <div className="flex items-center gap-2 text-[#9A762F] text-xs tracking-[0.28em] uppercase mb-3">
                  <Sparkles size={14} />
                  Accès Premium
                </div>
                <h2 className="font-serif-lux text-3xl md:text-4xl text-white leading-tight">
                  Ouvrez la porte de <span className="text-[#9A762F]">PROSPECT&rsquo;HEURE</span>
                </h2>
                <p className="text-sm text-[#667085] mt-3 leading-relaxed">
                  Renseignez vos coordonnées pour accéder à l&rsquo;entraînement officiel et
                  rejoindre la communauté internationale.
                </p>

                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="lead-capture-form">
                  <div>
                    <label className="text-xs text-[#667085] tracking-wider uppercase">
                      Nom complet
                    </label>
                    <input
                      required
                      value={form.full_name}
                      onChange={set("full_name")}
                      className="ph-input w-full px-3 py-2.5 rounded-sm text-sm mt-1"
                      placeholder="Prénom Nom"
                      data-testid="lead-input-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[#667085] tracking-wider uppercase">
                        E-mail
                      </label>
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        className="ph-input w-full px-3 py-2.5 rounded-sm text-sm mt-1"
                        placeholder="vous@email.com"
                        data-testid="lead-input-email"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#667085] tracking-wider uppercase">
                        WhatsApp
                      </label>
                      <input
                        required
                        value={form.whatsapp}
                        onChange={set("whatsapp")}
                        className="ph-input w-full px-3 py-2.5 rounded-sm text-sm mt-1"
                        placeholder="+243 900 000 000"
                        data-testid="lead-input-whatsapp"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[#667085] tracking-wider uppercase">
                        Pays
                      </label>
                      <select
                        required
                        value={form.country}
                        onChange={set("country")}
                        className="ph-input w-full px-3 py-2.5 rounded-sm text-sm mt-1"
                        data-testid="lead-input-country"
                      >
                        <option value="">Sélectionnez…</option>
                        {COUNTRIES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-[#667085] tracking-wider uppercase">
                        Ville
                      </label>
                      <input
                        required
                        value={form.city}
                        onChange={set("city")}
                        className="ph-input w-full px-3 py-2.5 rounded-sm text-sm mt-1"
                        placeholder="Kinshasa"
                        data-testid="lead-input-city"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="gold-btn w-full py-3 rounded-sm text-sm mt-2 disabled:opacity-60"
                    data-testid="lead-submit-btn"
                  >
                    {loading ? "Enregistrement…" : "Continuer vers l'accès"}
                  </button>

                  <div className="flex items-start gap-2 text-[11px] text-[#98A2B3] pt-1">
                    <ShieldCheck size={14} className="mt-0.5 text-[#9A762F]" />
                    <span>
                      Vos données sont enregistrées de manière sécurisée. Elles ne sont utilisées
                      que pour votre accès à PROSPECT&rsquo;HEURE.
                    </span>
                  </div>
                </form>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
